import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Card,
  Typography,
  Tag,
  message,
  Tooltip,
  Avatar,
  Row,
  Col,
  Statistic,
  Divider,
  Badge,
  Empty,
  Select,
  Segmented,
  Switch,
  Modal,
  Descriptions,
  Tabs,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ExperimentOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilterOutlined,
  SettingOutlined,
  EyeOutlined,
  FormOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  BarsOutlined,
  ShoppingOutlined,
  CopyOutlined,
  MinusOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getProductionUnits,
  deleteProductionUnit,
  duplicateProductionUnit,
  prefetchProductionOfflineBootstrap,
} from "./api";
import axios from "axios";
import { notification } from "antd";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import API_CONFIG from '../config/api.js';
import ProductionOnboardingModal from "./ProductionOnboardingModal";
import ProductionSetupAssistantModal from "./ProductionSetupAssistantModal";
import {
  getSelectedProductionTerminology,
  isSelectedCompanyProductionPackEligible,
} from "./productionTerminology";
import useIsMobile from "../hooks/useIsMobile";
import RowActionsMenu from "../components/RowActionsMenu";
import "./production-units.css";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const API_URL = API_CONFIG.BASE_URL;

// Définir des styles cohérents
const styles = {
  card: {
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    borderRadius: "8px",
    marginBottom: "16px",
  },
};

const PRODUCTION_KIND_SEMI_FINISHED = "semi_finished";
const getProductionKindLabel = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "Semi-fini" : "Fini";
const getProductionKindColor = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "gold" : "blue";

const formatNumberFr = (value, maxFractionDigits = 2) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: maxFractionDigits,
  }).format(Number(value));

const formatDateFr = (value) => {
  if (!value) {
    return "-";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleDateString("fr-FR");
};

const ProductionList = () => {
  const isMobile = useIsMobile();
  const [units, setUnits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedUnitDetails, setSelectedUnitDetails] = useState(null);
  const [onboardingModalVisible, setOnboardingModalVisible] = useState(false);
  const [onboardingInitialMode, setOnboardingInitialMode] = useState("suggestions");
  const [assistantModalVisible, setAssistantModalVisible] = useState(false);
  const [duplicatingUnitId, setDuplicatingUnitId] = useState(null);
  const effectiveViewMode = isMobile ? "cards" : viewMode;

  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  // Packs statiques "Packs rapides" + import de menu reserves a la restauration /
  // fast-food / boulangerie. Les autres profils gardent uniquement les recettes
  // suggerees (deja scopees par profil).
  const packEligible = isSelectedCompanyProductionPackEligible(
    user,
    selectedCompany
  );
  const canCreateProductionUnits = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Unites.create"
  );
  const canManageProductions =
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Gerer.view"
    ) ||
    hasPermission(
      "Gestion Commerciale.Approvisionnement.Production.Gerer.create"
    );
  const canUseMenuAi = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.ImporterMenu.ai"
  );
  const canCreateRawMaterials =
    hasPermission("Gestion Commerciale.Produits.Produits.view") &&
    hasPermission("Gestion Commerciale.Produits.Produits.create");
  const assistantQueryHandledRef = useRef(false);

  const selectedWarehouseName = useMemo(() => {
    if (!selectedWarehouse || !Array.isArray(user?.assigned_warehouses)) {
      return null;
    }

    const warehouse = user.assigned_warehouses.find(
      (item) => Number(item.id) === Number(selectedWarehouse)
    );

    return warehouse?.name || null;
  }, [selectedWarehouse, user]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenAssistant =
      searchParams.get("assistant") === "production-setup";

    if (!shouldOpenAssistant) {
      assistantQueryHandledRef.current = false;
      return;
    }

    if (!selectedWarehouse || assistantQueryHandledRef.current) {
      return;
    }

    assistantQueryHandledRef.current = true;
    setAssistantModalVisible(true);
  }, [location.search, selectedWarehouse]);

  // Fonction pour charger les unités de production
  const loadUnits = async (
    page = 1,
    limit = 10,
    search = "",
    status = null,
    warehouseId = null
  ) => {
    if (!warehouseId) {
      setUnits([]);
      setTotal(0);
      setLoading(false);
      return;
    }


    setLoading(true);
    try {
      // Simplify to focus on the core parameters
      let params = {
        page,
        limit,
        search,
      };

      // Add the warehouse ID as a direct filter parameter in a way the backend expects
      // Try the most common format for filtering
      params.warehouse_id = warehouseId;
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }

      if (status !== null) {
        params.status = status;
      }


      const data = await getProductionUnits(params);

      // If the units are returned but not filtered by warehouse, apply client-side filtering
      let filteredUnits = data.units;

      // Check if the API didn't filter by warehouse_id (by examining if units from other warehouses exist)
      const needsClientFiltering = filteredUnits.some((unit) => {
        // Check different possible warehouse ID fields
        const unitWarehouseId =
          unit.warehouse_id || (unit.warehouse && unit.warehouse.id);
        return unitWarehouseId && unitWarehouseId !== warehouseId;
      });

      // Apply client-side filter if needed
      if (needsClientFiltering) {
        filteredUnits = filteredUnits.filter((unit) => {
          // Handle different possible warehouse ID fields
          const unitWarehouseId =
            unit.warehouse_id || (unit.warehouse && unit.warehouse.id);
          return unitWarehouseId === warehouseId;
        });
      }

      // Also apply status filtering if needed
      if (status !== null && !params.hasOwnProperty("status")) {
        filteredUnits = filteredUnits.filter((unit) => unit.status === status);
      }

      setUnits(filteredUnits);
      setTotal(needsClientFiltering ? filteredUnits.length : data.total);
    } catch (error) {
      message.error(
        `Erreur lors du chargement des ${productionTerminology.unitPlural}`
      );
      setUnits([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Charger les unités au montage du composant ou quand refreshKey ou selectedWarehouse change
  useEffect(() => {

    if (selectedWarehouse) {
      loadUnits(
        pagination.current,
        pagination.pageSize,
        searchText,
        selectedStatus,
        selectedWarehouse
      );
    } else {
      setUnits([]);
      setTotal(0);
    }
  }, [
    refreshKey,
    selectedWarehouse,
    pagination.current,
    pagination.pageSize,
    searchText,
    selectedStatus,
    selectedCompany,
  ]);

  useEffect(() => {
    if (!selectedWarehouse || !selectedCompany) {
      return;
    }

    prefetchProductionOfflineBootstrap({
      warehouse_id: selectedWarehouse,
      company_id: selectedCompany,
    }).catch((error) => {
      console.error(
        "Unable to prefetch production offline bootstrap:",
        error
      );
    });
  }, [selectedWarehouse, selectedCompany]);

  // Gérer le changement de page
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Gérer la recherche
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer le filtre par statut
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setPagination({ ...pagination, current: 1 });
  };

  // Gérer la suppression d'une unité
  const handleDelete = async (id) => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.delete"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission de supprimer des ${productionTerminology.unitPlural}.`
      );
      return;
    }
    try {
      // Vérification du stock avant la suppression
      const unitToDelete = units.find((unit) => unit.id === id);

      // Si l'unité a un stock, empêcher la suppression
      if (unitToDelete && unitToDelete.current_stock > 0) {
        message.error(
          `Impossible de supprimer cette ${productionTerminology.unitSingular} car elle possède un stock. Veuillez d'abord épuiser le stock.`
        );
        return;
      }

      await deleteProductionUnit(id);
      message.success(
        `${productionTerminology.unitSingularTitle} supprimée avec succès`
      );
      setRefreshKey((oldKey) => oldKey + 1);
    } catch (error) {
      if (error.error) {
        message.error(error.error);
      } else {
        message.error(
          `Erreur lors de la suppression de ${productionTerminology.unitSingular}`
        );
      }
    }
  };

  // Basculement du statut d'une unité de production
  const handleToggleStatus = async (unit) => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission de modifier le statut des ${productionTerminology.unitPlural}.`
      );
      return;
    }
    const newStatus = unit.status === "active" ? "inactive" : "active";
    try {
      // Optimistic UI update
      setUnits((prev) =>
        prev.map((u) => (u.id === unit.id ? { ...u, status: newStatus } : u))
      );

      // API call to update status
      await axios.patch(`${API_URL}/api/production/units/${unit.id}/status`, {
        status: newStatus,
      });

      message.success("Statut mis à jour avec succès");
    } catch (error) {
      // Revert to original status on error
      setUnits((prev) =>
        prev.map((u) => (u.id === unit.id ? { ...u, status: unit.status } : u))
      );
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          "Erreur lors de la mise à jour du statut",
      });
    }
  };

  // Fonction pour afficher les details d'une unite
  const showUnitDetails = (unit) => {
    const formatDateTime = (value) => {
      if (!value) {
        return "N/A";
      }

      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime())
        ? "N/A"
        : parsedDate.toLocaleString("fr-FR");
    };

    const unitLabel = unit.unit_short_name || unit.unit_name || "unite(s)";

    const formatQuantity = (value, label = unitLabel) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "N/A";
      }
      return `${new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 2,
      }).format(Number(value))} ${label}`;
    };

    const formatCurrency = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "N/A";
      }
      return `${new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 2,
      }).format(Number(value))} CFA`;
    };

    const formatPercent = (value) => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "N/A";
      }
      return `${new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 2,
      }).format(Number(value))}%`;
    };

    const movementTypeLabel = (type) => {
      const labels = {
        sale: "Vente",
        purchase: "Achat",
        adjustment: "Ajustement",
        production: "Production",
        transfer: "Transfert",
        "stock-transfer": "Transfert",
        return: "Retour",
      };
      return labels[type] || type || "N/A";
    };

    const movementTypeColor = (type) => {
      const colors = {
        sale: "red",
        purchase: "green",
        adjustment: "orange",
        production: "blue",
        transfer: "purple",
        "stock-transfer": "purple",
        return: "cyan",
      };
      return colors[type] || "default";
    };

    const ModalContent = () => {
      const [activeTab, setActiveTab] = useState("details");
      const [history, setHistory] = useState([]);
      const [historyLoaded, setHistoryLoaded] = useState(false);
      const [loadingHistory, setLoadingHistory] = useState(false);
      const [historyPage, setHistoryPage] = useState(1);
      const [historyPageSize, setHistoryPageSize] = useState(10);
      const [unitDetails, setUnitDetails] = useState(null);
      const [loadingDetails, setLoadingDetails] = useState(false);
      const [detailsLoaded, setDetailsLoaded] = useState(false);

      useEffect(() => {
        if (detailsLoaded) {
          return;
        }

        setLoadingDetails(true);
        axios
          .get(`${API_URL}/api/production/units/${unit.id}`, {
            params: {
              company_id: unit.company_id,
              warehouse_id: selectedWarehouse,
            },
          })
          .then((response) => {
            setUnitDetails(response.data || null);
          })
          .catch((error) => {
            console.error("Erreur recuperation details unite:", error);
            message.error("Impossible de recuperer les details de l'unite.");
          })
          .finally(() => {
            setLoadingDetails(false);
            setDetailsLoaded(true);
          });
      }, [unit.id, detailsLoaded]);

      useEffect(() => {
        if (activeTab !== "history" || historyLoaded) {
          return;
        }

        const params = { product_id: unit.id, limit: 1000 };
        if (selectedWarehouse) {
          params.warehouse_id = selectedWarehouse;
        }

        setLoadingHistory(true);
        axios
          .get(`${API_URL}/api/stock-history`, { params })
          .then((response) => {
            const rows =
              response.data?.stock_history || response.data || [];
            setHistory(Array.isArray(rows) ? rows : []);
            setHistoryLoaded(true);
          })
          .catch((error) => {
            console.error("Erreur recuperation historique stock:", error);
            message.error("Impossible de recuperer l'historique des stocks.");
          })
          .finally(() => {
            setLoadingHistory(false);
          });
      }, [activeTab, historyLoaded, selectedWarehouse, unit.id]);

      const materials = unitDetails?.materials || [];
      const variableCosts = unitDetails?.variable_costs || [];
      const outputs = unitDetails?.outputs || [];
      const detailSource = {
        ...unit,
        ...(unitDetails || {}),
      };
      const detailUnitName = detailSource.unit_name
        ? detailSource.unit_short_name
          ? `${detailSource.unit_name} (${detailSource.unit_short_name})`
          : detailSource.unit_name
        : "N/A";
      const detailUnitLabel =
        detailSource.unit_short_name || detailSource.unit_name || unitLabel;
      const detailCreatedAt = formatDateTime(
        detailSource.created_at || unit.created_at
      );
      const detailUpdatedAt = formatDateTime(
        detailSource.updated_at || unit.updated_at
      );
      const productionCostValue = Number(detailSource.purchase_price);
      const salesPriceValue = Number(detailSource.sales_price);
      const marginValue =
        !Number.isNaN(productionCostValue) && !Number.isNaN(salesPriceValue)
          ? salesPriceValue - productionCostValue
          : null;
      const marginRate =
        marginValue !== null && salesPriceValue
          ? (marginValue / salesPriceValue) * 100
          : null;
      const marginDisplay =
        marginValue === null
          ? "N/A"
          : `${formatCurrency(marginValue)}${
              marginRate !== null ? ` (${formatPercent(marginRate)})` : ""
            }`;
      const statusTag = detailSource.status === "active" ? (
        <Tag color="green">Actif</Tag>
      ) : (
        <Tag color="red">Inactif</Tag>
      );
      const materialsCount = Array.isArray(unitDetails?.materials)
        ? unitDetails.materials.length
        : detailSource.materials_count || 0;
      const outputsCount = Array.isArray(unitDetails?.outputs)
        ? unitDetails.outputs.length
        : detailSource.outputs_count || 0;
      const detailSections = [
        {
          title: "Identification",
          items: [
            { label: "Nom", value: detailSource.name },
            { label: "Code article", value: detailSource.item_code || "N/A" },
            { label: "Slug", value: detailSource.slug || "N/A" },
            {
              label: `Type de ${productionTerminology.costContext}`,
              value: (
                <Tag color={getProductionKindColor(detailSource.production_kind)}>
                  {getProductionKindLabel(detailSource.production_kind)}
                </Tag>
              ),
            },
            { label: "Statut", value: statusTag },
            {
              label: "Description",
              value: detailSource.description || "Aucune description",
              span: 2,
            },
          ],
        },
        {
          title: "Organisation",
          items: [
            { label: "Entreprise", value: detailSource.company_name || "N/A" },
            {
              label: "Établissement",
              value:
                detailSource.warehouse_name ||
                detailSource.warehouse?.name ||
                "Non defini",
            },
            {
              label: "Categorie",
              value: detailSource.category_name || "Non definie",
            },
            { label: "Unite de mesure", value: detailUnitName },
          ],
        },
        {
          title: "Stock & prix",
          items: [
            {
              label: "Stock actuel",
              value: formatQuantity(detailSource.current_stock, detailUnitLabel),
            },
            {
              label: "Alerte stock",
              value: formatQuantity(
                detailSource.stock_quantitiy_alert,
                detailUnitLabel
              ),
            },
            {
              label: `Cout de ${productionTerminology.costContext}`,
              value: formatCurrency(detailSource.purchase_price),
            },
            { label: "Prix de vente", value: formatCurrency(detailSource.sales_price) },
            { label: "Marge beneficiaire", value: marginDisplay },
          ],
        },
        {
          title: "Composition",
          items: [
            {
              label: productionTerminology.materialPluralTitle,
              value: `${materialsCount} ${productionTerminology.materialPlural}`,
            },
            {
              label: productionTerminology.outputProducts,
              value: `${outputsCount} produit(s)`,
            },
          ],
        },
        {
          title: "Tracabilite",
          items: [
            { label: "Date de création", value: detailCreatedAt },
            { label: "Derniere modification", value: detailUpdatedAt },
            {
              label: "Symbologie code-barres",
              value: detailSource.barcode_symbology || "N/A",
            },
          ],
        },
      ];

      const formatItemQuantity = (value, item) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
          return "N/A";
        }
        const label = item?.unit_short_name || item?.unit_name || unitLabel;
        return `${new Intl.NumberFormat("fr-FR", {
          maximumFractionDigits: 2,
        }).format(Number(value))} ${label}`;
      };

      const materialColumns = [
        {
          title: "Matiere",
          key: "product_name",
          render: (text, record) => (
            <Space>
              {record.image ? (
                <Avatar size="small" src={record.image} />
              ) : (
                <Avatar size="small">
                  {record.product_name?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
              )}
              <span>{record.product_name || "-"}</span>
            </Space>
          ),
        },
        {
          title: "Quantite",
          dataIndex: "quantity",
          key: "quantity",
          align: "right",
          render: (value, record) => formatItemQuantity(value, record),
        },
        {
          title: "Prix achat",
          dataIndex: "purchase_price",
          key: "purchase_price",
          align: "right",
          render: (value) => formatCurrency(value),
        },
      ];

      const outputColumns = [
        {
          title: "Produit fini",
          key: "product_name",
          render: (text, record) => (
            <Space>
              {record.image ? (
                <Avatar size="small" src={record.image} />
              ) : (
                <Avatar size="small">
                  {record.product_name?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
              )}
              <span>{record.product_name || "-"}</span>
            </Space>
          ),
        },
        {
          title: "Quantite",
          dataIndex: "quantity",
          key: "quantity",
          align: "right",
          render: (value, record) => formatItemQuantity(value, record),
        },
        {
          title: "Prix vente",
          dataIndex: "sales_price",
          key: "sales_price",
          align: "right",
          render: (value) => formatCurrency(value),
        },
      ];

      const variableCostColumns = [
        {
          title: "Libelle",
          dataIndex: "label",
          key: "label",
          render: (value) => value || "-",
        },
        {
          title: "Cout unitaire",
          dataIndex: "cost_per_unit",
          key: "cost_per_unit",
          align: "right",
          render: (value) => formatCurrency(value),
        },
      ];

      const historyColumns = [
        {
          title: "Date",
          dataIndex: "created_at",
          key: "created_at",
          render: (value) => (value ? new Date(value).toLocaleString() : "-"),
        },
        {
          title: "Établissement",
          dataIndex: "warehouse_name",
          key: "warehouse_name",
          render: (value) => value || "-",
        },
        {
          title: "Quantite",
          dataIndex: "quantity",
          key: "quantity",
          render: (qty) => formatQuantity(qty),
        },
        {
          title: "Type",
          dataIndex: "movement_type",
          key: "movement_type",
          render: (type) => (
            <Tag color={movementTypeColor(type)}>{movementTypeLabel(type)}</Tag>
          ),
        },
        {
          title: "Details / Reference",
          key: "reference",
          render: (text, record) => {
            if (
              record.reference_type === "production_log" &&
              record.production_unit_name
            ) {
              return (
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {record.production_unit_name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    Ref: {record.reference_type}:{record.reference_id}
                  </div>
                </div>
              );
            }
            if (record.reference_type === "production_lot_loss") {
              return (
                <div>
                  <div style={{ fontWeight: 500 }}>Perte lot de production</div>
                  <div style={{ fontSize: "12px", color: "#888" }}>
                    Ref: {record.reference_type}:{record.reference_id}
                  </div>
                </div>
              );
            }
            if (record.reference_type && record.reference_id) {
              return `${record.reference_type}:${record.reference_id}`;
            }
            return "-";
          },
        },
        {
          title: "Mouvement",
          dataIndex: "quantity",
          key: "movement",
          render: (qty) => {
            if (qty > 0) {
              return (
                <Tag color="green" icon={<PlusOutlined />}>
                  Entree
                </Tag>
              );
            }
            if (qty < 0) {
              return (
                <Tag color="red" icon={<MinusOutlined />}>
                  Sortie
                </Tag>
              );
            }
            return <Tag color="default">Aucun</Tag>;
          },
        },
        {
          title: "Remarques",
          dataIndex: "remarks",
          key: "remarks",
          render: (remarks) => remarks || "-",
        },
      ];

      return (
        <div style={{ marginTop: "16px" }}>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Details" key="details">
              {detailSections.map((section, index) => (
                <div key={section.title} style={{ marginBottom: 16 }}>
                  {index > 0 && <Divider style={{ margin: "12px 0" }} />}
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    {section.title}
                  </Text>
                  <Descriptions bordered size="small" column={2}>
                    {section.items.map((item) => (
                      <Descriptions.Item
                        key={item.label}
                        label={item.label}
                        span={item.span || 1}
                      >
                        {item.value}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </div>
              ))}
              <Divider style={{ margin: "12px 0" }} />
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Details des compositions
              </Text>
              <Collapse
                bordered
                expandIcon={({ isActive }) =>
                  isActive ? <MinusOutlined /> : <PlusOutlined />
                }
              >
                <Panel
                  header={`${productionTerminology.materialPluralTitle} (${materials.length || 0})`}
                  key="materials"
                >
                  <Table
                    dataSource={materials}
                    columns={materialColumns}
                    loading={loadingDetails}
                    size="small"
                    pagination={{ pageSize: 5, showSizeChanger: true }}
                    rowKey={(record) =>
                      record.id
                        ? record.id.toString()
                        : record.product_id?.toString() || Math.random().toString()
                    }
                    locale={{
                      emptyText: loadingDetails
                        ? "Chargement..."
                        : productionTerminology.noMaterialFound,
                    }}
                  />
                </Panel>
                <Panel
                  header={`Produits finis (${outputs.length || 0})`}
                  key="outputs"
                >
                  <Table
                    dataSource={outputs}
                    columns={outputColumns}
                    loading={loadingDetails}
                    size="small"
                    pagination={{ pageSize: 5, showSizeChanger: true }}
                    rowKey={(record) =>
                      record.id
                        ? record.id.toString()
                        : record.product_id?.toString() || Math.random().toString()
                    }
                    locale={{
                      emptyText: loadingDetails
                        ? "Chargement..."
                        : "Aucun produit fini",
                    }}
                  />
                </Panel>
                <Panel
                  header={`Couts variables (${variableCosts.length || 0})`}
                  key="variable-costs"
                >
                  <Table
                    dataSource={variableCosts}
                    columns={variableCostColumns}
                    loading={loadingDetails}
                    size="small"
                    pagination={false}
                    rowKey={(record) =>
                      record.id
                        ? record.id.toString()
                        : `${record.label}-${record.cost_per_unit}`
                    }
                    locale={{
                      emptyText: loadingDetails
                        ? "Chargement..."
                        : "Aucun cout variable",
                    }}
                  />
                </Panel>
              </Collapse>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Historique du stock" key="history">
              <Table
                dataSource={history}
                columns={historyColumns}
                loading={loadingHistory}
                size="small"
                pagination={{
                  current: historyPage,
                  pageSize: historyPageSize,
                  total: history.length,
                  onChange: (page, pageSize) => {
                    setHistoryPage(page);
                    setHistoryPageSize(pageSize);
                  },
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                rowKey={(record) =>
                  record.id
                    ? record.id.toString()
                    : `${record.reference_type}-${record.reference_id}-${
                        record.created_at
                      }`
                }
                locale={{
                  emptyText: loadingHistory
                    ? "Chargement..."
                    : "Aucun mouvement de stock",
                }}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
      );
    };

    Modal.info({
      title: (
        <div style={{ display: "flex", alignItems: "center" }}>
          {unit.image ? (
            <Avatar src={unit.image} style={{ marginRight: "8px" }} />
          ) : (
            <Avatar style={{ backgroundColor: "#87d068", marginRight: "8px" }}>
              {unit.name.charAt(0).toUpperCase()}
            </Avatar>
          )}
            <span>{productionTerminology.detailsTitle}</span>
        </div>
      ),
      width: 900,
      content: <ModalContent />,
      okText: "Fermer",
    });
  };

  // Actualiser la liste
  const handleRefresh = () => {
    setSearchText("");
    setSelectedStatus(null);
    setPagination({ current: 1, pageSize: 10 });
    setRefreshKey((oldKey) => oldKey + 1);
  };

  // Basculer l'affichage des filtres
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const openOnboardingModal = (mode = "suggestions") => {
    setOnboardingInitialMode(mode);
    setOnboardingModalVisible(true);
  };

  const handleOpenAssistant = () => {
    setAssistantModalVisible(true);
  };

  const handleOpenRawMaterialsFromAssistant = () => {
    setAssistantModalVisible(false);

    const params = new URLSearchParams({
      assistant: "production-setup",
      action: "create-raw-material",
    });

    if (selectedWarehouse) {
      params.set("warehouse", String(selectedWarehouse));
    }

    navigate(`/gestion-produits/produits?${params.toString()}`);
  };

  const handleOpenUnitCreationFromAssistant = () => {
    setAssistantModalVisible(false);
    navigate("/production/units/create?assistant=production-setup");
  };

  const handleAdd = () => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.create"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission de créer des ${productionTerminology.unitPlural}.`
      );
      return;
    }
    navigate("/production/units/create");
  };

  const handleEdit = (unit) => {
    if (
      !hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
      )
    ) {
      message.error(
        `Vous n'avez pas la permission de modifier des ${productionTerminology.unitPlural}.`
      );
      return;
    }
    navigate(`/production/units/edit/${unit.id}`, { state: { unit } });
  };

  const handleDuplicate = async (unit) => {
    if (!canCreateProductionUnits) {
      message.error(
        `Vous n'avez pas la permission de créer des ${productionTerminology.unitPlural}.`
      );
      return;
    }

    try {
      setDuplicatingUnitId(unit.id);
      const duplicatedUnit = await duplicateProductionUnit(unit.id);

      message.success(
        `${productionTerminology.unitSingularTitle} dupliquée avec succès sous le nom "${duplicatedUnit.name}".`
      );

      if (duplicatedUnit?.id) {
        navigate(`/production/units/edit/${duplicatedUnit.id}`);
        return;
      }

      setRefreshKey((oldKey) => oldKey + 1);
    } catch (error) {
      message.error(
        error?.error ||
          `Erreur lors de la duplication de ${productionTerminology.unitSingular}`
      );
    } finally {
      setDuplicatingUnitId(null);
    }
  };

  const buildUnitActionItems = (record, includeProductionAction = true) => {
    const actionItems = [];
    const isDuplicating = duplicatingUnitId === record.id;

    if (includeProductionAction) {
      actionItems.push({
        key: "produce",
        icon: <ExperimentOutlined />,
        label: productionTerminology.actionVerb,
        onClick: () => navigate(`/production/process/${record.id}`),
      });
    }

    actionItems.push({
      key: "view",
      icon: <EyeOutlined />,
      label: "Voir le détail",
      onClick: () => showUnitDetails(record),
    });

    if (canCreateProductionUnits) {
      actionItems.push({
        key: "duplicate",
        icon: <CopyOutlined />,
        label: isDuplicating ? "Duplication en cours…" : "Dupliquer",
        disabled: isDuplicating,
        onClick: () => handleDuplicate(record),
      });
    }

    if (
      hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.edit"
      )
    ) {
      actionItems.push({
        key: "edit",
        icon: <EditOutlined />,
        label: "Modifier",
        onClick: () => handleEdit(record),
      });
    }

    if (
      hasPermission(
        "Gestion Commerciale.Approvisionnement.Production.Unites.delete"
      )
    ) {
      actionItems.push(
        { type: "divider" },
        {
          key: "delete",
          icon: <DeleteOutlined />,
          label: "Supprimer",
          danger: true,
          onClick: () =>
            Modal.confirm({
              title: `Êtes-vous sûr de vouloir supprimer cette ${productionTerminology.unitSingular} ?`,
              okText: "Oui",
              cancelText: "Non",
              okButtonProps: { danger: true },
              onOk: () => handleDelete(record.id),
            }),
        }
      );
    }

    return actionItems;
  };

  // Colonnes du tableau
  const columns = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space className="production-units__identity">
          {record.image ? (
            <Avatar src={record.image} />
          ) : (
            <Avatar className="production-units__avatar">
              {text.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Text strong>{text}</Text>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
      fixed: "left",
      width: 280,
    },
    {
      title: "Type",
      dataIndex: "production_kind",
      key: "production_kind",
      align: "center",
      width: 110,
      render: (kind) => (
        <Tag color={getProductionKindColor(kind)}>
          {getProductionKindLabel(kind)}
        </Tag>
      ),
      sorter: (a, b) =>
        getProductionKindLabel(a.production_kind).localeCompare(
          getProductionKindLabel(b.production_kind)
        ),
      responsive: ["md"],
    },
    {
      title: "Date de cr\u00e9ation",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => <span className="production-units__date">{formatDateFr(value)}</span>,
      sorter: (a, b) =>
        (new Date(a.created_at || 0).getTime() || 0) -
        (new Date(b.created_at || 0).getTime() || 0),
      responsive: ["sm"],
      width: 165,
    },
    {
      title: "Coût de revient unitaire (défaut)",
      dataIndex: "default_unit_production_cost",
      key: "default_unit_production_cost",
      align: "right",
      render: (value, record) => {
        const costValue = Number(value ?? record.purchase_price);
        if (Number.isNaN(costValue)) {
          return (
            <Text type="secondary" italic>
              Non defini
            </Text>
          );
        }
        return `${formatNumberFr(costValue)} CFA`;
      },
      sorter: (a, b) => {
        const valueA = Number(
          a.default_unit_production_cost ?? a.purchase_price ?? 0
        );
        const valueB = Number(
          b.default_unit_production_cost ?? b.purchase_price ?? 0
        );
        return valueA - valueB;
      },
      width: 240,
      responsive: ["lg"],
    },
    {
      title: "Stock actuel",
      dataIndex: "current_stock",
      key: "current_stock",
      align: "center",
      render: (stock, record) => {
        const stockValue = stock || 0;
        const alertValue = record.stock_quantitiy_alert || 0;
        const isLow = stockValue <= alertValue && stockValue > 0;
        const isEmpty = stockValue === 0;

        let color = "green";
        if (isLow) color = "orange";
        if (isEmpty) color = "red";

        return (
          <Tooltip
            title={
              isLow
                ? "Stock faible"
                : isEmpty
                ? "Stock épuisé"
                : "Stock disponible"
            }
          >
            <Tag
              color={color}
              style={{ borderRadius: "12px", padding: "0 8px" }}
            >
              {stockValue} {record.unit_short_name || "unité(s)"}
            </Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => (a.current_stock || 0) - (b.current_stock || 0),
      width: 150,
      responsive: ["sm"],
    },
    {
      title: "Produits",
      dataIndex: "outputs_count",
      key: "outputs_count",
      align: "center",
      render: (count) => (
        <Tag color="green" style={{ borderRadius: "12px", padding: "0 8px" }}>
          {count}
        </Tag>
      ),
      sorter: (a, b) => a.outputs_count - b.outputs_count,
      responsive: ["lg"],
      width: 100,
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 140,
      render: (text, record) => (
        <Switch
          aria-label={`Statut de ${record.name}`}
          checkedChildren="Actif"
          unCheckedChildren="Inactif"
          checked={record.status === "active"}
          disabled={!hasPermission("Gestion Commerciale.Approvisionnement.Production.Unites.edit")}
          onChange={() => handleToggleStatus(record)}
        />
      ),
      filters: [
        { text: "Actif", value: "active" },
        { text: "Inactif", value: "inactive" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 88,
      fixed: "right",
      render: (_, record) => (
        <RowActionsMenu
          items={buildUnitActionItems(record)}
          loading={duplicatingUnitId === record.id}
          ariaLabel={`Afficher les actions de ${productionTerminology.unitSingular} ${
            record.name || record.id
          }`}
        />
      ),
    },
  ];

  // Fonction pour transformer les données du tableau en vue de cartes
  const renderCardView = () => {
    if (units.length === 0 && !loading) {
      return <Empty description={productionTerminology.noUnitFound} />;
    }
    return (
      <Row gutter={[16, 16]}>
        {units.map((unit) => (
          <Col xs={24} sm={12} md={12} lg={8} xl={6} key={unit.id}>
            <Card
              hoverable
              style={{
                marginBottom: 16,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
              styles={{ body: { flexGrow: 1 } }}
              cover={
                <div
                  style={{
                    padding: "16px",
                    background: "var(--bg-tertiary)",
                    textAlign: "center",
                    borderBottom: "1px solid var(--border-color)",
                  }}
                >
                  <ExperimentOutlined
                    style={{ fontSize: "48px", color: "var(--color-info)" }}
                  />
                  <Title
                    level={5}
                    style={{ marginTop: 8, marginBottom: 0 }}
                    className="text-base truncate"
                  >
                    {unit.name}
                  </Title>
                </div>
              }
              actions={[
                <RowActionsMenu
                  key={`actions-${unit.id}`}
                  items={buildUnitActionItems(unit, false)}
                  loading={duplicatingUnitId === unit.id}
                  ariaLabel={`Afficher les actions de ${productionTerminology.unitSingular} ${
                    unit.name || unit.id
                  }`}
                />,
              ]}
            >
              <Card.Meta
                description={
                  <Tooltip title={unit.description || "Aucune description"}>
                    <Text
                      type="secondary"
                      style={{
                        height: 44,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {unit.description || "Aucune description"}
                    </Text>
                  </Tooltip>
                }
              />
              <div style={{ marginTop: "auto", paddingTop: "10px" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Tag color={unit.status === "active" ? "green" : "red"}>
                    {unit.status === "active" ? "Actif" : "Inactif"}
                  </Tag>
                  <Tag color={getProductionKindColor(unit.production_kind)}>
                    {`Type: ${getProductionKindLabel(unit.production_kind)}`}
                  </Tag>
                  <Text type="secondary">
                    Stock: {unit.current_stock || 0}{" "}
                    {unit.unit_short_name || "unités"}
                  </Text>
                </Space>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // Statistiques résumées
  const renderSummaryStats = () => {
    // Compter le nombre d'unités actives et inactives
    const activeUnits = units.filter((unit) => unit.status === "active").length;

    // Calculer le nombre total de matières premières et produits finis
    const totalMaterials = units.reduce(
      (sum, unit) => sum + unit.materials_count,
      0
    );
    const totalOutputs = units.reduce(
      (sum, unit) => sum + unit.outputs_count,
      0
    );

    // Calculer le stock total disponible
    const totalStock = units.reduce(
      (sum, unit) => sum + (unit.current_stock || 0),
      0
    );

    // Trouver l'unité de mesure la plus commune
    const unitCounts = {};
    units.forEach((unit) => {
      if (unit.unit_short_name) {
        unitCounts[unit.unit_short_name] =
          (unitCounts[unit.unit_short_name] || 0) + 1;
      }
    });
    let mostCommonUnit = "unités";
    if (Object.keys(unitCounts).length > 0) {
      mostCommonUnit = Object.keys(unitCounts).reduce((a, b) =>
        unitCounts[a] > unitCounts[b] ? a : b
      );
    }

    const metrics = [
      { title: productionTerminology.unitsTitle, value: units.length, icon: <BarChartOutlined /> },
      { title: `${productionTerminology.unitsTitleShort} actives`, value: activeUnits, icon: <Badge status="success" /> },
      { title: productionTerminology.materialPluralTitle, value: totalMaterials, icon: <FileExcelOutlined /> },
      { title: "Produits finis", value: totalOutputs, icon: <FormOutlined /> },
      { title: "Stock total disponible", value: totalStock, suffix: mostCommonUnit, icon: <ShoppingOutlined /> },
    ];

    return (
      <section className="production-units__summary" aria-label="Synthèse de la page" aria-busy={loading}>
        <div className="production-units__summary-scope">Sur cette page</div>
        <div className="production-units__metrics">
          {metrics.map(({ title, value, suffix, icon }) => (
            <div className="production-units__metric" key={title}>
              <span className="production-units__metric-icon" aria-hidden="true">{icon}</span>
              <Statistic title={title} value={value} suffix={suffix} />
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderOnboardingCallout = () => {
    if (loading || units.length > 0 || !canCreateProductionUnits) {
      return null;
    }

    return (
      <Card
        className="production-units__onboarding"
      >
        <Space
          direction="vertical"
          size={8}
          style={{ width: "100%", alignItems: "flex-start" }}
        >
          <Title level={5} style={{ margin: 0 }}>
            Initialisation rapide
          </Title>
          <Text type="secondary">
            Vous pouvez précharger un pack type pour créer des {productionTerminology.materialPlural} et des{" "}
            {productionTerminology.configurationPlural} de base sans modifier les workflows existants.
          </Text>
          <Space wrap>
            <Button
              icon={<FormOutlined />}
              onClick={handleOpenAssistant}
              disabled={!canCreateProductionUnits}
            >
              Assistant de configuration
            </Button>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() =>
                openOnboardingModal(packEligible ? "templates" : "suggestions")
              }
            >
              Initialiser un pack
            </Button>
          </Space>
        </Space>
      </Card>
    );
  };

  // Si aucun magasin n'est sélectionné, afficher un message
  if (!selectedWarehouse) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Card style={styles.card}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Typography.Text strong>
                Veuillez sélectionner un établissement dans la barre de navigation en
                haut à droite pour afficher les {productionTerminology.unitPlural}.
              </Typography.Text>
            }
          />
        </Card>
      </div>
    );
  }


  return (
    <div className="production-units-container production-units">
      <header className="production-units__header">
        <div className="production-units__heading">
          <h1>{productionTerminology.unitsTitle}</h1>
          <p>Gérez vos {productionTerminology.unitPlural}</p>
        </div>
        <div className="production-units__actions">
          <Button
            icon={<RocketOutlined aria-hidden="true" />}
            onClick={() => navigate("/production/process/batch")}
            disabled={!canManageProductions || !selectedWarehouse}
          >
            Produire en lot
          </Button>
          <Button icon={<FormOutlined aria-hidden="true" />} onClick={handleOpenAssistant} disabled={!canCreateProductionUnits}>
            Assistant
          </Button>
          <Button
            icon={<SettingOutlined aria-hidden="true" />}
            onClick={() => openOnboardingModal(packEligible ? "templates" : "suggestions")}
            disabled={!canCreateProductionUnits}
          >
            Initialiser un pack
          </Button>
          <Button type="primary" icon={<PlusOutlined aria-hidden="true" />} onClick={handleAdd} disabled={!canCreateProductionUnits}>
            {productionTerminology.addUnitAction}
          </Button>
        </div>
      </header>

      {renderSummaryStats()}

      <div className="production-units__toolbar" role="search" aria-label={`Recherche des ${productionTerminology.unitPlural}`}>
        <Input
          className="production-units__search"
          type="search"
          prefix={<SearchOutlined aria-hidden="true" />}
          aria-label={`Rechercher une ${productionTerminology.unitSingular}`}
          placeholder={`Rechercher une ${productionTerminology.unitSingular}...`}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
        />
        <div className="production-units__tools">
          <Button
            icon={<FilterOutlined aria-hidden="true" />}
            onClick={toggleFilters}
            type={showFilters ? "primary" : "default"}
            aria-expanded={showFilters}
            aria-controls="production-units-filters"
          >
            Filtres
          </Button>
          <Button icon={<ReloadOutlined aria-hidden="true" />} onClick={handleRefresh}>
            Actualiser
          </Button>
          {!isMobile && (
            <Segmented
              aria-label="Mode d'affichage"
              options={[
                { value: "table", label: <Tooltip title="Vue liste"><BarsOutlined aria-label="Vue liste" /></Tooltip> },
                { value: "cards", label: <Tooltip title="Vue cartes"><AppstoreOutlined aria-label="Vue cartes" /></Tooltip> },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
          )}
        </div>
      </div>
      {showFilters && (
        <div className="production-units__filters" id="production-units-filters">
          <label htmlFor="production-units-status">Statut</label>
          <Select
            id="production-units-status"
            placeholder="Tous les statuts"
            allowClear
            onChange={handleStatusChange}
            value={selectedStatus}
            options={[{ value: "active", label: "Actif" }, { value: "inactive", label: "Inactif" }]}
          />
        </div>
      )}

      {renderOnboardingCallout()}

      <section className="production-units__results" aria-label={productionTerminology.unitsTitle}>
        {effectiveViewMode === "table" ? (
          <Table
            className="production-units__table"
            columns={columns}
            dataSource={units}
            rowKey="id"
            size="middle"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              showSizeChanger: true,
              showTotal: (count) => `Total ${count} ${productionTerminology.unitPlural}`,
            }}
            loading={loading}
            onChange={handleTableChange}
            bordered={false}
            scroll={{ x: 1200 }}
          />
        ) : (
          <div className="production-units__cards">{renderCardView()}</div>
        )}
      </section>

      <ProductionOnboardingModal
        visible={onboardingModalVisible}
        warehouseId={selectedWarehouse}
        warehouseName={selectedWarehouseName}
        companyId={selectedCompany}
        packEligible={packEligible}
        canUseMenuAi={canUseMenuAi}
        initialMode={onboardingInitialMode}
        terminology={productionTerminology}
        onCancel={() => setOnboardingModalVisible(false)}
        onApplied={() => {
          setOnboardingModalVisible(false);
          setRefreshKey((prev) => prev + 1);
        }}
      />
      <ProductionSetupAssistantModal
        visible={assistantModalVisible}
        warehouseId={selectedWarehouse}
        companyId={selectedCompany}
        productionUnitCount={total}
        canCreateRawMaterials={canCreateRawMaterials}
        canCreateProductionUnits={canCreateProductionUnits}
        terminology={productionTerminology}
        onCancel={() => setAssistantModalVisible(false)}
        onOpenOnboarding={(mode) => {
          setAssistantModalVisible(false);
          openOnboardingModal(mode);
        }}
        onOpenRawMaterials={handleOpenRawMaterialsFromAssistant}
        onOpenUnitCreation={handleOpenUnitCreationFromAssistant}
      />
    </div>
  );
};

export default ProductionList;
